import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { ExperienceExportResponse } from '@/types/experience-export'
import {
  buildExperienceLibraryRecord,
  getExperienceLibraryRecord,
  listExperienceLibraryRecords,
  replaceExperienceLibraryRecords,
  saveExperienceLibraryRecord,
  upsertExperienceLibraryRecord,
  type ExperienceLibraryRecord,
} from '@/utils/experienceLibrary'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

async function tryGetAuthHeaders(): Promise<Record<string, string> | null> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
    if (!token) return null
    return { Authorization: `Bearer ${token}` }
  } catch {
    return null
  }
}

function normalizeRemoteRecord(record: ExperienceLibraryRecord): ExperienceLibraryRecord {
  return {
    ...record,
    localKey: record.localKey || (record as any).id,
    familyId: record.familyId || record.lineageId || record.quizConfig?.experienceDefinitionId || record.localKey || (record as any).id,
    recordType: record.recordType || (record.status === 'published' ? 'published-version' : 'draft'),
    lineageId: record.lineageId || record.quizConfig?.experienceDefinitionId || record.localKey || (record as any).id,
    versionNumber: record.versionNumber || (record.quizConfig?.experienceDefinitionVersionId ? Number.parseInt(record.quizConfig.experienceDefinitionVersionId.replace(/^v/, ''), 10) || 1 : 1),
  }
}

export const experiencesService = {
  async syncLibrary(includeArchived = true): Promise<ExperienceLibraryRecord[] | null> {
    const headers = await tryGetAuthHeaders()
    if (!headers) return null

    try {
      const response = await axios.get<{ success: boolean; data: ExperienceLibraryRecord[] }>(
        `${API_URL}/manager/experiences`,
        {
          headers,
          params: { includeArchived: includeArchived ? 'true' : 'false' },
        },
      )

      const remoteRecords = (response.data.data || []).map(normalizeRemoteRecord)
      const localRecords = listExperienceLibraryRecords({ includeArchived: true })
      const merged = [
        ...remoteRecords,
        ...localRecords.filter((localRecord) => !remoteRecords.some((remoteRecord) => remoteRecord.localKey === localRecord.localKey)),
      ]
      replaceExperienceLibraryRecords(merged)
      return merged
    } catch (error) {
      console.warn('Experience library sync fell back to local storage', error)
      return listExperienceLibraryRecords({ includeArchived })
    }
  },

  async getExperience(experienceId: string): Promise<ExperienceLibraryRecord | null> {
    const headers = await tryGetAuthHeaders()
    if (!headers) {
      return getExperienceLibraryRecord(experienceId)
    }

    try {
      const response = await axios.get<{ success: boolean; data: ExperienceLibraryRecord }>(
        `${API_URL}/manager/experiences/${encodeURIComponent(experienceId)}`,
        { headers },
      )

      const record = normalizeRemoteRecord(response.data.data)
      const synced = (await this.syncLibrary(true)) || []
      replaceExperienceLibraryRecords([
        record,
        ...synced.filter((item) => item.localKey !== record.localKey),
      ])
      return record
    } catch (error) {
      console.warn('Experience fetch fell back to local storage', error)
      return getExperienceLibraryRecord(experienceId)
    }
  },

  async saveExperience(record: ExperienceLibraryRecord, options?: { optimisticLocal?: boolean }): Promise<ExperienceLibraryRecord> {
    const headers = await tryGetAuthHeaders()
    const shouldOptimisticallyPersist = options?.optimisticLocal ?? true
    const localRecord = shouldOptimisticallyPersist
      ? (upsertExperienceLibraryRecord(record) || saveExperienceLibraryRecord({
          quizConfig: record.quizConfig,
          campaignName: record.campaignName,
          seed: record,
        }) || record)
      : (buildExperienceLibraryRecord({
          quizConfig: record.quizConfig,
          campaignName: record.campaignName,
          seed: record,
        }) || record)

    if (!headers) {
      if (!shouldOptimisticallyPersist) {
        return saveExperienceLibraryRecord({
          quizConfig: record.quizConfig,
          campaignName: record.campaignName,
          seed: record,
        }) || localRecord
      }
      return localRecord
    }

    try {
      const response = await axios.put<{ success: boolean; data: ExperienceLibraryRecord }>(
        `${API_URL}/manager/experiences/${encodeURIComponent(localRecord.localKey)}`,
        localRecord,
        { headers },
      )

      const saved = normalizeRemoteRecord(response.data.data)
      await this.syncLibrary(true)
      return saved
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        throw error
      }
      console.warn('Experience save fell back to local storage', error)
      return localRecord
    }
  },

  async setArchived(experienceId: string, archived: boolean): Promise<void> {
    const headers = await tryGetAuthHeaders()
    if (!headers) return

    try {
      await axios.post(
        `${API_URL}/manager/experiences/${encodeURIComponent(experienceId)}/archive`,
        { archived },
        { headers },
      )

      await this.syncLibrary(true)
    } catch (error) {
      console.warn('Experience archive change fell back to local storage', error)
    }
  },

  async deleteExperience(experienceId: string): Promise<boolean> {
    const headers = await tryGetAuthHeaders()
    if (!headers) return false

    try {
      await axios.delete(
        `${API_URL}/manager/experiences/${encodeURIComponent(experienceId)}`,
        { headers },
      )

      await this.syncLibrary(true)
      return true
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        await this.syncLibrary(true)
        return false
      }
      console.warn('Experience delete failed', error)
      throw error
    }
  },

  async getExperienceExport(lineageId: string): Promise<ExperienceExportResponse> {
    const headers = await tryGetAuthHeaders()
    if (!headers) throw new Error('Not authenticated')

    const response = await axios.get<{ success: boolean; data: ExperienceExportResponse }>(
      `${API_URL}/manager/experiences/${encodeURIComponent(lineageId)}/export`,
      { headers },
    )

    return response.data.data
  },
}
